import React, {useLayoutEffect, useRef} from 'react'
import ParticleField from './ParticleField'
import {
  motion,
  MotionValue,
  useMotionValue,
  useSpring,
  useTransform,
  Variants,
} from 'framer-motion'
import {toAbsoluteUrl} from '../../../_metronic/helpers'

const RED = '#E63946'
const CYAN = '#00B4D8'
// Cores extraídas do texto do logo (INSIGHT / EXPERIENCE)
const LOGO_RED = '#FF3149'
const LOGO_CYAN = '#00B5ED'
const LOGO_URL = toAbsoluteUrl('/media/logos/logo-data-analytics.png')
const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1]

// Deslocamento proporcional à posição do mouse (efeito de profundidade)
const useParallax = (value: MotionValue<number>, distance: number) =>
  useTransform(value, [-0.5, 0.5], [-distance, distance])

/* ------------------------------------------------------------------ */
/* Fundo                                                               */
/* ------------------------------------------------------------------ */

function ShootingStar({top, left, delay}: {top: string; left: string; delay: number}) {
  return (
    <motion.span
      className='absolute h-px w-40 origin-right'
      style={{
        top,
        left,
        rotate: -30,
        background: `linear-gradient(90deg, transparent, ${CYAN}AA, #ffffff)`,
        filter: `drop-shadow(0 0 6px ${CYAN})`,
      }}
      initial={{opacity: 0, x: 0, y: 0}}
      animate={{opacity: [0, 1, 0], x: [0, 320], y: [0, -185]}}
      transition={{duration: 1.4, delay, repeat: Infinity, repeatDelay: 7, ease: 'easeOut'}}
    />
  )
}

/* ------------------------------------------------------------------ */
/* Logo                                                                */
/* ------------------------------------------------------------------ */

function Logo() {
  return (
    <motion.div
      className='relative mb-10'
      initial={{opacity: 0, scale: 0.7, filter: 'blur(14px)'}}
      animate={{opacity: 1, scale: 1, filter: 'blur(0px)'}}
      transition={{duration: 1.1, ease: EASE_OUT}}
    >
      {/* Flutuação suave após a entrada: eixos com durações diferentes para não parecer mecânico */}
      <motion.div
        className='relative'
        animate={{y: [0, -12, 0], x: [0, 4, 0, -4, 0], rotate: [0, 1, 0, -1, 0]}}
        transition={{
          delay: 1.2,
          y: {duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.2},
          x: {duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 1.2},
          rotate: {duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 1.2},
        }}
      >
        <img
          src={LOGO_URL}
          alt='Insight Experience'
          className='h-20 md:h-28 w-auto select-none'
          draggable={false}
        />
        {/* Brilho que varre apenas os pixels do logo */}
        <motion.span
          aria-hidden
          className='pointer-events-none absolute inset-0'
          style={{
            WebkitMaskImage: `url(${LOGO_URL})`,
            maskImage: `url(${LOGO_URL})`,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            backgroundImage:
              'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
            backgroundSize: '250% 100%',
            backgroundRepeat: 'no-repeat',
          }}
          initial={{backgroundPosition: '150% 0'}}
          animate={{backgroundPosition: ['150% 0', '-50% 0']}}
          transition={{duration: 1.6, delay: 1.4, repeat: Infinity, repeatDelay: 5, ease: 'easeInOut'}}
        />
      </motion.div>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/* Título                                                              */
/* ------------------------------------------------------------------ */

const titleContainer: Variants = {
  hidden: {},
  show: {transition: {staggerChildren: 0.12, delayChildren: 0.45}},
}

const titleWord: Variants = {
  hidden: {opacity: 0, y: 40, rotateX: -70, filter: 'blur(8px)'},
  show: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: {duration: 0.9, ease: EASE_OUT},
  },
}

function Title() {
  return (
    <motion.h1
      className='text-4xl md:text-6xl font-bold text-slate-800 tracking-tight'
      style={{perspective: 800}}
      variants={titleContainer}
      initial='hidden'
      animate='show'
    >
      {['Bem-vindo', 'ao'].map((w) => (
        <motion.span key={w} className='inline-block mr-[0.28em]' variants={titleWord}>
          {w}
        </motion.span>
      ))}
      {/* Mesmas cores do texto do logo */}
      <motion.span className='inline-block mr-[0.28em]' style={{color: LOGO_RED}} variants={titleWord}>
        Insight
      </motion.span>
      <motion.span className='inline-block' style={{color: LOGO_CYAN}} variants={titleWord}>
        Experience
      </motion.span>
    </motion.h1>
  )
}

/* ------------------------------------------------------------------ */
/* Página                                                              */
/* ------------------------------------------------------------------ */

// Ajusta a altura para ocupar exatamente o espaço livre da janela, descontando
// header, footer e paddings do layout, para a página nunca gerar scroll vertical.
function useFitViewport(ref: React.RefObject<HTMLDivElement>) {
  useLayoutEffect(() => {
    const fit = () => {
      const el = ref.current
      if (!el) return
      el.style.height = `${window.innerHeight}px`
      const overflow = document.documentElement.scrollHeight - window.innerHeight
      el.style.height = `${Math.max(320, Math.floor(window.innerHeight - overflow))}px`
    }
    fit()
    // O header pode mudar de altura depois do primeiro paint (fontes, menu do usuário)
    const late = window.setTimeout(fit, 400)
    window.addEventListener('resize', fit)
    return () => {
      window.clearTimeout(late)
      window.removeEventListener('resize', fit)
    }
  }, [ref])
}

export default function IexpHome() {
  const rootRef = useRef<HTMLDivElement>(null)
  useFitViewport(rootRef)

  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const sx = useSpring(mouseX, {stiffness: 50, damping: 20})
  const sy = useSpring(mouseY, {stiffness: 50, damping: 20})

  const fgX = useParallax(sx, 10)
  const fgY = useParallax(sy, 10)

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set((e.clientX - r.left) / r.width - 0.5)
    mouseY.set((e.clientY - r.top) / r.height - 0.5)
  }

  return (
    <div
      ref={rootRef}
      className='flex items-center justify-center relative overflow-hidden'
      style={{height: 'calc(100vh - 131px)'}}
      onPointerMove={onPointerMove}
      onPointerLeave={() => {
        mouseX.set(0)
        mouseY.set(0)
      }}
    >
      {/* Camada de fundo: grade de pontos */}
      <div className='absolute inset-0 pointer-events-none'>
        <motion.div
          className='absolute inset-0'
          style={{
            backgroundImage: 'radial-gradient(rgba(23,50,77,0.13) 1px, transparent 1px)',
            backgroundSize: '26px 26px',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 10%, transparent 65%)',
            maskImage: 'radial-gradient(ellipse at center, black 10%, transparent 65%)',
          }}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{duration: 1.5}}
        />
      </div>

      {/* Camada intermediária: constelação de partículas e estrelas cadentes */}
      <div className='absolute inset-0 pointer-events-none'>
        <ParticleField />
        <ShootingStar top='28%' left='8%' delay={3} />
        <ShootingStar top='75%' left='55%' delay={6.5} />
      </div>

      {/* Conteúdo */}
      <motion.div
        className='relative z-10 flex flex-col items-center justify-center px-6 text-center'
        style={{x: fgX, y: fgY}}
      >
        <Logo />

        <div className='space-y-6'>
          <Title />
          <motion.p
            initial={{opacity: 0, y: 16, filter: 'blur(6px)'}}
            animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
            transition={{duration: 0.9, delay: 0.95, ease: EASE_OUT}}
            className='text-slate-600 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed'
          >
            Sua jornada para insights transformadores começa aqui.
            Prepare-se para uma experiência única e inovadora.
          </motion.p>
        </div>

      </motion.div>

      {/* Fade inferior */}
      <div className='absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#e9edf1] to-transparent pointer-events-none' />
    </div>
  )
}
